/**
 * Feature descriptor — the frontend half of the plug-in/plug-out architecture
 * (docs/adr/002-modular-feature-registry.md). Each feature slice exports one of
 * these; the registry builds the router and nav from the enabled subset.
 */
import type { ComponentType, LazyExoticComponent } from "react";
import type { LucideIcon } from "lucide-react";

/** Which global scope controls (header locality / time-range selectors) a route
 * opts into. Omit or leave a field falsy to hide that control — e.g. single-entity
 * detail pages (locality/sensor detail) are already route-scoped, and utility pages
 * (settings, simulator) have no time/locality dimension. */
export interface ScopeControls {
  locality?: boolean;
  days?: boolean;
  /**
   * Whether arriving here without an explicit `?locality=` should inherit the
   * last-used locality (default `true`).
   *
   * Set `false` for a page whose figures are cross-referenced against the
   * system-wide dashboard: inheriting a locality would show the same metric with
   * a different value than the page the user just came from. Such a route starts
   * system-wide and keeps its locality selection to itself — it never overwrites
   * the remembered locality that the sharing pages use.
   */
  inheritLocality?: boolean;
  /**
   * The route always shows the system-wide view over the default window, whatever the
   * remembered scope is (the Dashboard). Visiting it makes that the remembered scope,
   * so every page reached from it — the sidebar, a banner link, a feed link — opens on
   * the same scope and the same figures instead of a locality or range picked earlier
   * on another page.
   */
  systemWide?: boolean;
}

/** Additional route owned by a feature but not shown as a top-level nav item
 * (e.g. a detail page or a sub-tab). React Router ranks static segments above
 * dynamic (":id") ones, so order between sub-routes does not matter. */
export interface SubRoute {
  path: string;
  element: LazyExoticComponent<ComponentType>;
  /** Scope controls for this sub-route (defaults to none → controls hidden). */
  scope?: ScopeControls;
}

/**
 * A feature-owned surface mounted into the shell chrome rather than the router.
 *
 * Some features are not pages. An assistant drawer, for instance, has to be
 * reachable from every route and has no URL of its own — registering it as a
 * `FeatureDescriptor` would mount a route that renders nothing. Importing it
 * directly into `AppShell` would be worse: the shell would gain a hardcoded
 * dependency on one feature, which is exactly what the registry exists to
 * prevent (docs/adr/002-modular-feature-registry.md).
 *
 * So the shell renders whatever widgets are enabled, generically, and the widget
 * owns its own trigger and any surfaces it opens. Gating reuses the same flag map
 * and role rules as routed features.
 */
export interface ShellWidgetDescriptor {
  /** Stable id; must match a key in the backend feature-flag map. */
  id: string;
  /** Feature-flag key gating this widget (usually === id). */
  flag: string;
  /** Roles allowed to see it (undefined = any authenticated user). */
  roles?: string[];
  /**
   * Component mounted in the header's action cluster. It is rendered inside a
   * `backdrop-filter` ancestor, so any panel it opens must use a portal rather
   * than `position: fixed` (see ChatWidget for why).
   */
  element: LazyExoticComponent<ComponentType>;
}

/**
 * Sidebar sections, rendered in this order.
 *
 * The nav was a flat list of fourteen destinations in which `Settings`, `MLOps`
 * and `Alerts` carried equal weight. Grouping is by *what the reader came to do*:
 * `operate` is today's work, `analyse` is understanding it, `admin` is running the
 * platform itself, `help` is reference material (the glossary). A feature declares its own group, so the shell never names one.
 */
export const NAV_GROUPS = ["operate", "analyse", "admin", "help"] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

export interface FeatureDescriptor {
  /** Stable id; must match a key in the backend feature-flag map. */
  id: string;
  /** Feature-flag key gating this feature (usually === id). */
  flag: string;
  /** Route path as used by NavLink (e.g. "/" or "/alerts"). */
  path: string;
  /** Sidebar section this feature belongs to. */
  group: NavGroup;
  /** True for the index ("/") route. */
  index?: boolean;
  /** i18n key for the sidebar label. */
  navKey: string;
  /** Sidebar icon. */
  icon: LucideIcon;
  /** Roles allowed to see/reach this feature (undefined = any authenticated user). */
  roles?: string[];
  /** Lazily-loaded page component (code-split per feature). */
  element: LazyExoticComponent<ComponentType>;
  /** Set false to mount the route but hide it from the nav. */
  showInNav?: boolean;
  /** Extra routes owned by this feature (detail/sub pages), gated by the same flag/roles. */
  subRoutes?: SubRoute[];
  /** Global scope controls shown on this feature's index route (defaults to none). */
  scope?: ScopeControls;
}
